import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router';
import { ApiProvider, useBranding } from '@fms/api-client';
import {
  AppThemeProvider,
  BrandingThemeVars,
  LiveAnnouncerProvider,
  useBootSplashTeardown,
} from '@fms/ui';
import '@fms/ui/tokens.css';
import './i18n';
import { routes } from './router';
import { SessionProvider } from './session';

const API_BASE =
  (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:4100/api/v1';

const router = createBrowserRouter(routes);

/**
 * Public surfaces an anonymous visitor must be able to browse freely. The
 * service catalogue is NOT one of them any more — it sits behind the login with
 * everything else, so only tracking (ARN + CAPTCHA is the credential) and the
 * login screen itself stay open.
 */
const PUBLIC_PATHS = [/^\/track/, /^\/auth\//];

/**
 * A lost session only interrupts the user on a protected route; on public
 * pages (catalog, tracking) it is silent — guards handle access, not this.
 */
function handleAuthLost() {
  const path = window.location.pathname;
  if (PUBLIC_PATHS.some((re) => re.test(path))) return;
  void router.navigate(`/auth/login?next=${encodeURIComponent(path)}`);
}

/**
 * Publishes the operator's brand colours as CSS custom properties. Renders
 * nothing while they are the shipped defaults, so the common case costs zero.
 */
function BrandingVars() {
  const { data } = useBranding();
  return data ? <BrandingThemeVars theme={data.branding.theme} /> : null;
}

function App() {
  useBootSplashTeardown();
  return (
    <StrictMode>
      <AppThemeProvider>
        <LiveAnnouncerProvider>
          <ApiProvider
            options={{ baseURL: API_BASE, portal: 'citizen', onAuthLost: handleAuthLost }}
          >
            <BrandingVars />
            <SessionProvider>
              <RouterProvider router={router} />
            </SessionProvider>
          </ApiProvider>
        </LiveAnnouncerProvider>
      </AppThemeProvider>
    </StrictMode>
  );
}

// Hydrate prerendered HTML when present, else a fresh client render.
const root = document.getElementById('root')!;
if (root.hasChildNodes()) {
  hydrateRoot(root, <App />);
} else {
  createRoot(root).render(<App />);
}
